import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "locales";
import {
  AiFeatureDisabledError,
  askMemory,
  buildCitation,
  buildRecordingChatLink,
  formatClock,
  MemoryAnswer,
  MemorySource,
  searchMemory,
} from "lib/aiApi";
import "./style.scss";

interface Props {
  /** Optional thread scope. When omitted the search covers all the user's
      accessible recordings + messages. */
  threadId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

type Mode = "search" | "ask";

const MemoryPanel: React.FC<Props> = ({ threadId = null, isOpen, onClose, className }) => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState("");
  const [mode, setMode] = useState<Mode>("ask");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [answer, setAnswer] = useState<MemoryAnswer | null>(null);
  const [sources, setSources] = useState<MemorySource[]>([]);

  // Close on Escape — common UX for side-panels.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const submit = useCallback(async () => {
    const q = question.trim();
    if (!q || loading) return;

    setLoading(true);
    setError(null);
    setAnswer(null);
    setSources([]);

    try {
      if (mode === "ask") {
        const r = await askMemory(q, { threadId });
        setAnswer(r);
        setSources(r.sources);
      } else {
        const r = await searchMemory(q, { threadId });
        setSources(r.sources);
      }
    } catch (err) {
      if (err instanceof AiFeatureDisabledError) {
        setDisabled(true);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
    }
  }, [question, mode, threadId, loading]);

  if (!isOpen) return null;

  return (
    <aside className={`memory-panel ${className || ""}`}>
      <header className="memory-panel__header">
        <h3 className="memory-panel__title">🧠 {t("Meeting Memory")}</h3>
        <button className="memory-panel__close" onClick={onClose} aria-label={t("Close")}>
          ×
        </button>
      </header>

      {disabled ? (
        <div className="memory-panel__notice">
          {t("Memory is disabled on this server.")} {t("Set MEMORY_ENABLED=true after provisioning the embedding service and AI database.")}
        </div>
      ) : (
        <>
          <div className="memory-panel__mode">
            <button
              className={`memory-panel__tab ${mode === "ask" ? "memory-panel__tab--active" : ""}`}
              onClick={() => setMode("ask")}
            >
              {t("Ask")}
            </button>
            <button
              className={`memory-panel__tab ${mode === "search" ? "memory-panel__tab--active" : ""}`}
              onClick={() => setMode("search")}
            >
              {t("Search")}
            </button>
            {threadId && (
              <span className="memory-panel__scope">{t("scope: this chat")}</span>
            )}
          </div>

          <form
            className="memory-panel__form"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <input
              type="text"
              className="memory-panel__input"
              placeholder={
                mode === "ask"
                  ? t("Ask anything about your meetings…")
                  : t("Find phrases, decisions, names…")
              }
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              autoFocus
            />
            <button
              type="submit"
              className="memory-panel__submit"
              disabled={!question.trim() || loading}
            >
              {loading ? "…" : mode === "ask" ? t("Ask") : t("Search")}
            </button>
          </form>

          {error && <div className="memory-panel__error">{error}</div>}

          {answer && (
            <div className="memory-panel__answer">
              <div className="memory-panel__answer-text">{answer.answer}</div>
            </div>
          )}

          {sources.length > 0 && (
            <div className="memory-panel__sources">
              <span className="memory-panel__sources-label">
                {mode === "ask" ? t("Sources") : t("Top matches")}
              </span>
              <ul className="memory-panel__source-list">
                {sources.map((s) => (
                  <SourceRow key={`${s.kind}-${s.recordingId || s.messageId}-${s.index}`} source={s} />
                ))}
              </ul>
            </div>
          )}

          {!loading && !answer && sources.length === 0 && !error && (
            <div className="memory-panel__hint">
              {t("Tip: ask \"What did we decide about pricing?\" or \"When did we discuss the launch?\".")}
            </div>
          )}
        </>
      )}
    </aside>
  );
};

const SourceRow: React.FC<{ source: MemorySource }> = ({ source }) => {
  const { t } = useTranslation();
  const isTranscript = source.kind === "TRANSCRIPT";
  const chatLink = buildRecordingChatLink(source.threadId);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildCitation(source));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be blocked in non-secure contexts; fall back silently
    }
  };

  return (
    <li className="memory-panel__source">
      <header className="memory-panel__source-head">
        <span className="memory-panel__source-index">[{source.index}]</span>
        {isTranscript ? (
          <>
            <span className="memory-panel__source-kind">{t("recording")}</span>
            {source.speakerName && (
              <span className="memory-panel__source-author">{source.speakerName}</span>
            )}
            {source.startSec != null && (
              <span className="memory-panel__source-time">@ {formatClock(source.startSec)}</span>
            )}
          </>
        ) : (
          <>
            <span className="memory-panel__source-kind memory-panel__source-kind--message">
              {t("chat")}
            </span>
            {source.threadId && (
              <span className="memory-panel__source-author">{t("thread {{id}}", { id: source.threadId })}</span>
            )}
          </>
        )}
        <span className="memory-panel__source-similarity">
          {(source.similarity * 100).toFixed(0)}%
        </span>
      </header>
      <p className="memory-panel__source-text">{source.text}</p>
      <div className="memory-panel__source-actions">
        {chatLink && (
          <a className="memory-panel__source-link" href={chatLink}>
            {t("Open chat ↗")}
          </a>
        )}
        <button
          type="button"
          className="memory-panel__source-link memory-panel__source-link--btn"
          onClick={handleCopy}
        >
          {copied ? t("✓ Copied") : t("Copy citation")}
        </button>
      </div>
    </li>
  );
};

export default MemoryPanel;
